import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSessionInfo } from '../../hooks/useSessionInfo';
import RTButton from '../common/RTButton';
import './TermsAndConditions.css';
import tenantStorage from '../../utils/tenantStorage';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { displayName } = useSessionInfo();

  // Check login status on component mount
  useEffect(() => {
    const accessToken = tenantStorage.getItem('accessToken');
    setIsLoggedIn(!!accessToken);
  }, []);

  const handleButtonClick = () => {
      navigate(-1);
  };

  return (
    <div className="terms-container">
      <div className="terms-header">
        <h1>Terms and Conditions</h1>
        <div className="terms-last-updated">Last updated: August 15, 2025</div>
      </div>
      
      <div className="terms-content">
        <section className="terms-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By using RentTick hostel management system developed for {displayName}, you agree to be bound by these Terms and Conditions. 
            These terms apply to all residents, guardians, and users of our hostel services and payment systems.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Hostel Accommodation Services</h2>
          <h3>2.1 Bed Allocation</h3>
          <p>
            Bed allocation is subject to availability and is managed through our digital system. 
            Room assignments may be changed at the discretion of hostel management for operational reasons.
          </p>
          <h3>2.2 Occupancy Rules</h3>
          <ul>
            <li>Each resident is allocated one bed in a shared or private room</li>
            <li>Subletting or unauthorized sharing of accommodation is strictly prohibited</li>
            <li>Guests must be registered with management and follow visitor policies</li>
            <li>Check-in and check-out times must be adhered to as specified</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>3. Payment Terms and Conditions</h2>
          <h3>3.1 Fee Structure</h3>
          <p>
            Hostel fees include accommodation charges, maintenance fees, and applicable taxes. 
          </p>
          <h3>3.2 Payment Schedule</h3>
          <ul>
            <li>Monthly rent is due on the 1st of each month</li>
            <li>Security deposit is required before check-in</li>
            <li>Late payment fees apply after the 5th of each month</li>
            <li>Advance payment discounts may be available for semester/annual payments</li>
          </ul>
          <h3>3.3 Accepted Payment Methods</h3>
          <p>We accept the following payment methods through our secure payment gateway:</p>
          <ul>
            <li>Credit Cards (Visa, MasterCard, American Express)</li>
            <li>Debit Cards</li>
            <li>Net Banking</li>
            <li>UPI (Unified Payments Interface)</li>
            <li>Digital Wallets (Paytm, PhonePe, Google Pay)</li>
            <li>Bank Transfers (NEFT/RTGS)</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>4. Payment Gateway and Security</h2>
          <h3>4.1 Third-Party Payment Processors</h3>
          <p>
            Payments are processed through certified payment gateway providers including but not limited to 
            Razorpay, Payu, PhonePe, and Stripe. These providers maintain PCI DSS compliance and use industry-standard 
            encryption to protect your financial information.
          </p>
          <h3>4.2 Payment Security</h3>
          <ul>
            <li>All transactions are encrypted using SSL/TLS protocols</li>
            <li>We do not store complete credit card information on our servers</li>
            <li>Payment confirmations are sent via email and SMS</li>
            <li>Transaction disputes should be reported within 48 hours</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>5. Refund and Cancellation Policy</h2>
          <h3>5.1 Security Deposit Refund</h3>
          <p>
            Security deposits will be refunded within 15-30 working days after check-out, 
            subject to deductions for damages, outstanding dues, or cleaning charges.
          </p>
          <h3>5.2 Monthly Rent Refund</h3>
          <ul>
            <li>No refund for partial month occupancy unless due to hostel facility issues</li>
            <li>Medical emergency departures may be considered for partial refunds with proper documentation</li>
            <li>Academic semester completion refunds are processed as per institutional calendar</li>
          </ul>
          <h3>5.3 Refund Processing</h3>
          <p>
            Approved refunds are credited within 7-14 business days to the original payment method. 
            Bank processing times may vary and are beyond our control.
          </p>
        </section>

        <section className="terms-section">
          <h2>6. Communication and Notifications</h2>
          <h3>6.1 WhatsApp Messaging</h3>
          <p>
            By using RentTick services, you consent to receive automated WhatsApp messages for important 
            notifications and updates. These messages are sent to enhance your experience and keep you 
            informed about critical matters.
          </p>
          <h3>6.2 Types of WhatsApp Notifications</h3>
          <ul>
            <li><strong>Payment Reminders:</strong> Automated reminders about upcoming payment due dates and overdue payments</li>
            <li><strong>Allocation Notifications:</strong> Alerts when your bed allocation starts and upcoming expiry reminders</li>
            <li><strong>Important Updates:</strong> Critical information about hostel operations, facilities, or policies</li>
            <li><strong>Transaction Confirmations:</strong> Payment receipts and invoice notifications</li>
          </ul>
          <h3>6.3 Communication Guidelines</h3>
          <ul>
            <li>You must provide a valid WhatsApp-enabled mobile number during registration</li>
            <li>It is your responsibility to keep your contact information updated</li>
            <li>Messages are sent during reasonable hours (typically 9 AM to 8 PM)</li>
            <li>You may opt-out of non-essential notifications by contacting management</li>
            <li>Critical notifications (payment reminders, allocation updates) cannot be opted out while you are a resident</li>
          </ul>
          <h3>6.4 Other Communication Channels</h3>
          <p>
            In addition to WhatsApp, we may communicate through email, SMS, phone calls, or in-app 
            notifications for important matters. Email and SMS confirmations are also sent for payments.
          </p>
        </section>

        <section className="terms-section">
          <h2>7. Paying Guest Rights and Responsibilities</h2>
          <h3>7.1 Resident Rights</h3>
          <ul>
            <li>Safe and secure accommodation</li>
            <li>Access to common facilities during operational hours</li>
            <li>Privacy and peaceful enjoyment of allocated space</li>
            <li>Timely maintenance and housekeeping services</li>
            <li>Transparent billing and fee structure</li>
          </ul>
          <h3>7.2 Resident Responsibilities</h3>
          <ul>
            <li>Maintain cleanliness and hygiene in personal and common areas</li>
            <li>Respect other residents and follow community guidelines</li>
            <li>Report maintenance issues promptly</li>
            <li>Comply with hostel timings and visitor policies</li>
            <li>Take care of hostel property and equipment</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>8. Termination and Eviction</h2>
          <p>
            The hostel reserves the right to terminate accommodation for violations of terms, 
            non-payment of dues, or behavior detrimental to the hostel community. 
            30 days notice will be provided except in cases of serious misconduct.
          </p>
        </section>

        <section className="terms-section">
          <h2>9. Limitation of Liability</h2>
          <p>
            The hostel's liability is limited to the accommodation services provided. 
            We are not responsible for personal belongings, loss, or damages except where 
            proven to be due to hostel negligence.
          </p>
        </section>

        <section className="terms-section">
          <h2>10. Privacy and Data Protection</h2>
          <p>
            Your privacy is important to us. For detailed information about how we collect, use, protect, 
            and handle your personal information and payment data, please refer to our{' '}
            <Link to="/privacy" className="terms-link">Privacy Policy</Link>.
          </p>
          <p>
            The Privacy Policy covers important topics including:
          </p>
          <ul>
            <li>What information we collect and how we use it</li>
            <li>How we protect your payment and personal data</li>
            <li>Your rights regarding your personal information</li>
            <li>How we share information with service providers</li>
            <li>Data retention and security measures</li>
            <li>WhatsApp messaging and communication preferences</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>11. Dispute Resolution</h2>
          <p>
            Disputes will be resolved through internal grievance procedures first. 
            If unresolved, matters will be subject to the jurisdiction of local courts 
            as per the hostel's registered address.
          </p>
        </section>

        <section className="terms-section">
          <h2>12. Modifications to Terms</h2>
          <p>
            These terms may be updated periodically. Residents will be notified of significant changes 
            via email or notice board announcements. Continued use of services constitutes acceptance of modified terms.
          </p>
        </section>

        <section className="terms-section">
          <h2>13. Contact Information</h2>
          <p>
            For questions regarding these terms, payment issues, or general inquiries, 
            please contact hostel management through the RentTick system or at the reception desk.
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> heliatick.it@gmail.com</p>
            <p><strong>Phone:</strong> +91 9789340423</p>
          </div>
          <p>
            For detailed contact information including phone numbers, email addresses, and office hours, 
            please visit our <a href="/contact" className="terms-link">Contact Us page</a>.
          </p>
        </section>

        <section className="terms-section">
          <h2>14. Application Development and Maintenance</h2>
          <p>
            The RentTick hostel management system is developed and maintained by <strong>HeliaTick</strong>. 
            For technical support, feature requests, or system-related inquiries, 
            please reach out through the appropriate channels provided within the application.
          </p>
        </section>
      </div>
      
      <div className="terms-footer">
        <RTButton variant="primary" onClick={handleButtonClick}>
          Close
        </RTButton>
      </div>
    </div>
  );
};

export default TermsAndConditions;
